<template>
  <div class="space-y-2 w-full">
    <div
      class="border rounded-md w-full overflow-hidden relative touch-none select-none bg-white"
      :style="{
        height: height + 'px',
      }"
    >
      <canvas ref="canvasRef" class="w-full h-full"></canvas>
      <!-- optional transparent overlay to capture pointer events on mobile -->
    </div>

    <div class="flex gap-2">
      <Button variant="outline" size="sm" @click="handleClear">Clear</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import SignaturePad from 'signature_pad'
import { Button } from '@/components/ui/button'

interface Props {
  modelValue?: string
  height?: number
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  height: 200,
})
const emit = defineEmits<{
  'update:modelValue': [string]
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const pad = ref<SignaturePad | null>(null)

onMounted(() => {
  if (!canvasRef.value) return
  const canvas = canvasRef.value
  resizeCanvas(canvas)
  const signaturePad = new SignaturePad(canvas, {
    backgroundColor: 'rgba(255,255,255,0)',
  })
  pad.value = signaturePad

  // If there is an initial value, load it
  if (props.modelValue) {
    signaturePad.fromDataURL(props.modelValue)
  }

  signaturePad.addEventListener('endStroke', () => {
    // wait for the stroke to be drawn before updating the model value
    setTimeout(() => {
      emit('update:modelValue', signaturePad.toDataURL())
    }, 100)
  })

  // listen window resize to keep resolution
  window.addEventListener('resize', () => resizeCanvas(canvas))
})

watch(
  () => props.modelValue,
  (val) => {
    if (!pad.value) return
    if (pad.value.isEmpty()) {
      if (val) pad.value.fromDataURL(val)
    }
  },
)

function handleClear() {
  pad.value?.clear()
  emit('update:modelValue', '')
}

function resizeCanvas(canvas: HTMLCanvasElement) {
  const ratio = Math.max(window.devicePixelRatio || 1, 1)
  canvas.width = canvas.offsetWidth * ratio
  canvas.height = props.height * ratio
  canvas.getContext('2d')?.scale(ratio, ratio)
}
</script>
